import { getCookie, setCookie } from '@shared/lib';
import { recordServerInternalUser } from '@/shared/lib/analytics/internal-user';

import { apiWithoutAuth } from './instances';
import { clearLegacyRefreshToken, readLegacyRefreshTokenKey } from './legacy-refresh-cookie';

import type { ApiResponse, Token } from '@shared/types';

/**
 * TRAD-3683 — 토큰 재발급(reissue) 공용 경로. handlers.ts(401 응답 복구)와 AuthContext(마운트 시 선제 갱신)가
 * 같은 함수를 부른다(종전엔 두 곳에 본문이 복사돼 있었고, 둘 다 실패 이유를 안 보고 쿠키를 지웠다).
 *
 * ## TRAD-3861 — 리프레시 토큰은 서버 HttpOnly 쿠키(backend TRAD-3860 · 계약 docs-ops/2026-10-29-TRAD-3860-cookie-contract.md)
 * ~~web 이 `refreshToken` JS 쿠키를 읽어 body `refreshTokenKey` 로 보내고, 응답의 새 키를 다시 JS 쿠키에 저장~~ →
 *  - body 는 `{ userId }` 만. 키는 브라우저가 **HttpOnly 쿠키**(`Path=/api/auth`)로 자동 동봉한다(같은 오리진 XHR · `X-Requested-With` 는
 *    axios 기본 헤더 — 없으면 서버 403 AUTH_014).
 *  - 응답 `refreshToken` 은 **어떤 값이 와도 저장하지 않는다**(1단계 창에서 body 키로 간 요청엔 값이 올 수 있다 — 계약 §2). 새 키는 Set-Cookie 로 회전.
 *  - 이행기: 옛 JS 쿠키 `refreshToken` 이 남아 있으면 **1회 body 에 동봉**(서버 쿠키 우선 → body 폴백) → 성공하면 그 쿠키를 지운다.
 *  - ~~«실패 후 재확인»(보낸 키 ≠ 지금 JS 쿠키 키면 OTHER_TAB)~~ 삭제 — 키를 더는 읽을 수 없다. 대신 서버 유예 30초(TRAD-3682 `RefreshTokenGrace`)
 *    + 공용 쿠키 저장소(이긴 탭의 Set-Cookie 가 진 탭에도 적용)가 경합을 구제한다. Web Locks 직렬화와 «잠금 안 accessToken 재확인»은 그대로.
 *
 * ## 1. 실패 이유를 본다 — 401·404 만 «세션 끝»
 * 정본: iOS TokenRequestInterceptor.performTokenRefresh(`case 401, 404` → 로그아웃, 그 외·응답 없음 → 토큰 유지) ·
 * Android TokenAuthenticator(TRAD-3645 «401·404 만 세션 종료»). backend 도 같다 — 키 무효 AUTH_006/AUTH_005 = 401,
 * 회원 없음 USER_001 = 404. 네트워크 오류·타임아웃·5xx·403(AUTH_014 포함) 은 «지금 못 갱신했다»일 뿐 세션이 끝난 게 아니다.
 *
 * 쿠키를 «지우는» 것은 호출부 몫이다 — 이 함수는 성공 시 새 accessToken 을 «저장»만 한다.
 */

export type ReissueFailureOutcome = 'LOGOUT' | 'KEEP';

export type ReissueResult =
  | { kind: 'TOKEN'; accessToken: string; source: 'REISSUED' | 'OTHER_TAB' }
  | { kind: 'FAILED'; outcome: ReissueFailureOutcome; error: unknown };

/** 순수 판정 — reissue 실패를 «세션 끝(LOGOUT)» / «토큰 유지(KEEP)» 로 가른다. 응답이 없으면(오프라인·타임아웃) KEEP. */
export const classifyReissueFailure = (error: unknown): ReissueFailureOutcome => {
  const status = (error as { response?: { status?: unknown } } | null | undefined)?.response?.status;
  return status === 401 || status === 404 ? 'LOGOUT' : 'KEEP';
};

export const REISSUE_LOCK_NAME = 'xtrading:token-reissue';

const ACCESS_TOKEN_MAX_AGE = 60 * 15; // backend TTL 15분

type LockManagerLike = { request: <T>(name: string, callback: () => Promise<T>) => Promise<T> };

/** Web Locks 가 있으면 탭 간 직렬, 없으면 그냥 실행(폴백 — 서버 유예 30초가 경합을 막는다). */
const withCrossTabLock = <T>(run: () => Promise<T>): Promise<T> => {
  const locks = (globalThis.navigator as (Navigator & { locks?: LockManagerLike }) | undefined)?.locks;
  return typeof locks?.request === 'function' ? locks.request(REISSUE_LOCK_NAME, run) : run();
};

export type ReissueSessionInput = {
  userId: string | undefined;
  /**
   * 실패한 요청이 실었던 accessToken.
   *  - 문자열: 그 토큰으로 401 을 받았다 → 지금 쿠키가 다르면 다른 탭이 갱신한 것
   *  - null: accessToken 없이 시작했다(선제 갱신) → 지금 쿠키에 토큰이 생겼으면 다른 탭이 갱신한 것
   *  - undefined: 모른다(요청에 Authorization 헤더가 없었다) → 재확인 없이 reissue(종전 동작)
   */
  staleAccessToken: string | null | undefined;
  secure: boolean;
};

/** 재발급 요청 body — 순수 함수(테스트용 export). 옛 JS 쿠키 키가 있을 때만 `refreshTokenKey` 를 동봉한다(이행기 1회). */
export const buildReissueBody = (userId: string | undefined, legacyKey: string | undefined) =>
  legacyKey ? { userId, refreshTokenKey: legacyKey } : { userId };

const tokenFromOtherTab = (stale: string | null | undefined): string | null => {
  if (stale === undefined) return null;
  const current = getCookie('accessToken');
  return current && current !== stale ? current : null;
};

export const reissueSession = ({ userId, staleAccessToken, secure }: ReissueSessionInput): Promise<ReissueResult> =>
  withCrossTabLock(async (): Promise<ReissueResult> => {
    const already = tokenFromOtherTab(staleAccessToken);
    if (already) return { kind: 'TOKEN', accessToken: already, source: 'OTHER_TAB' };

    const legacyKey = readLegacyRefreshTokenKey();
    try {
      const { data } = await apiWithoutAuth.post<ApiResponse<Token>>(
        '/api/auth/reissue-access-token',
        buildReissueBody(userId, legacyKey),
      );
      // 🔴 응답의 refreshToken 은 읽지도 저장하지도 않는다 — 새 키는 서버 Set-Cookie(HttpOnly)로만 회전한다.
      const { accessToken } = data.result;
      setCookie('accessToken', accessToken, { path: '/', secure, maxAge: ACCESS_TOKEN_MAX_AGE });
      // 이행기 — 옛 JS 쿠키 키로 성공했으면 서버 쿠키로 전환된 것 → 옛 쿠키 제거(다음부턴 body 키 0).
      if (legacyKey) clearLegacyRefreshToken();
      // TRAD-3847 — 재발급 응답 internalUser(true 일 때만 sticky). 자동 로그인으로 들어온 팀 기기도 잡힌다.
      recordServerInternalUser(data.result.internalUser);
      return { kind: 'TOKEN', accessToken, source: 'REISSUED' };
    } catch (error) {
      return { kind: 'FAILED', outcome: classifyReissueFailure(error), error };
    }
  });
